<template>
  <div :class="{ 'address-form-open': isAddressFormShown }">
    <template v-if="isLoqateCountry">
      <AddressSuggestionsSearch
        v-bind="addressSuggestionsSearchProps"
        :always-show-address-form="alwaysShowAddressForm"
        :label-text="loqateLabelText"
        :placeholder="placeholder"
        :address-suggestion-error="addressSuggestionError"
        @input="$emit('input', $event)"
        @select="$emit('select', $event)"
        @clear="$emit('clear')"
        @focus="$emit('focus')"
        @fill-address-manually="$emit('fill-address-manually')"
      />
      <hr class="my-8">
    </template>
    <form
      v-if="isAddressFormShown"
      id="address-form"
      v-loading="loading"
      :class="{ 'mt-3': isLoqateCountry }"
      class="address-form"
      @submit.prevent="onSubmit"
    >
      <div :class="`address-grid address-grid-${countryCode}`">
        <div v-for="item in formElements" :key="item.model" class="w-full" :class="item.class" :style="setGridArea(item.model)">
          <OSelect
            v-if="item.type === 'select'"
            :id="item.model"
            v-model.trim="form[item.model]"
            :label="setInputLabel(item.label)"
            :options="item.options"
            :required="item.validations.required"
            :error="getErrorMessage(item) || errors[item.model]"
            :disabled="disabled"
            @input="onChange(null, item.model)"
          />
          <OInput
            v-else
            :id="item.model"
            v-model.trim="form[item.model]"
            :type="item.type"
            :required="item.validations.required"
            :label="setInputLabel(item.label)"
            :input-classes="['w-full']"
            :error="getErrorMessage(item) || errors[item.model]"
            :disabled="disabled"
            :exclude-symbols="item.excludeSymbols"
            @change="onChange"
          />
        </div>
      </div>
      <template
        v-if="!hideCountry"
      >
        <OSelect
          :label="$t('Form.Address.country')"
          :default-svg="countryCode"
          :value="selectedCountry"
          :options="countryList"
          svg-classes="rounded"
          :clearable="false"
          disabled
          required
        />
        <p class="pt-2 ota-small-light text-left text-gray-500" v-text="$t(`SignUp.StepFour.Required`)" />
      </template>
      <div
        v-if="!hideSubmit"
        class="flex mt-7"
        :class="[...submitButtonClasses, cancelLink ? 'justify-end' : 'justify-center']"
      >
        <OButton
          v-if="cancelLink"
          :disabled="loading"
          button-type="border"
          variant="dark"
          size="sm"
          class="mr-2 md:w-auto"
          @click="onCancel"
        >
          {{ cancelText.length ? cancelText : $t('FormNavigation.Cancel') }}
        </OButton>
        <OButton
          type="submit"
          :disabled="loading"
          variant="secondary"
          :attribute="{ name: 'data-qa', value: 'signup-confirm-address' }"
          :size="buttonSize"
          :class="{ 'ml-2': cancelLink }"
        >
          {{ $t(successButtonText) }}
        </OButton>
      </div>
    </form>
  </div>
</template>

<script>
import merge from 'lodash/merge'
import { required, maxLength, numeric } from 'vuelidate/lib/validators'
import { CountryNames, CountryCodes, FormFields } from '@/enums/general'
import i18nConfig from '@/config/i18n'
import { ButtonSizes } from '@/enums/components'
const OInput = () => import('@/components/OInput')
const OButton = () => import('@/components/OButton')
const OSelect = () => import('@/components/OSelect')
const AddressSuggestionsSearch = () => import('@/components/Loqate/AddressSuggestionsSearch')

export default {
  name: 'AddressForm',
  components: {
    OSelect,
    OButton,
    OInput,
    AddressSuggestionsSearch
  },
  props: {
    address: {
      type: Object,
      default: () => {}
    },
    hideSubmit: {
      type: Boolean,
      default: false
    },
    countryCode: {
      type: String,
      default: i18nConfig.defaultCountry
    },
    cancelLink: {
      type: [String, Boolean],
      default: false
    },
    cancelText: {
      type: String,
      default: ''
    },
    hideCountry: {
      type: Boolean,
      default: false
    },
    isLoqateCountry: {
      type: Boolean,
      default: false
    },
    successButtonText: {
      type: String,
      default: 'FormNavigation.Save'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    submitButtonClasses: {
      type: Array,
      default: () => ([])
    },
    loading: {
      type: Boolean,
      default: false
    },
    showAddressForm: {
      type: Boolean,
      default: true
    },
    alwaysShowAddressForm: {
      type: Boolean,
      default: false
    },
    buttonSize: {
      type: String,
      default: ButtonSizes.small
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    addressSuggestionsSearchProps: {
      type: Object,
      default: () => ({})
    },
    placeholder: {
      type: String,
      default: ''
    },
    addressSuggestionError: {
      type: String,
      default: ''
    }
  },
  validations () {
    const rules = {}

    this.formElements.forEach((element) => {
      if (element.show) {
        const name = element.model
        const obj = {
          [name]: {}
        }
        if (element.validations.required === true) {
          const validateRequired = {
            [name]: {
              required
            }
          }
          merge(obj, validateRequired)
        }

        if (element.validations.numeric === true) {
          const validateNumeric = {
            [name]: {
              numeric
            }
          }
          merge(obj, validateNumeric)
        }

        if (element.validations.maxLength) {
          const validateMaxLength = {
            [name]: {
              maxLength: maxLength(element.validations.maxLength)
            }
          }

          merge(obj, validateMaxLength)
        }

        if (element.validations.regex) {
          const validatePostalCode = {
            [name]: {
              format (value) {
                return element.validations.regex.test(value)
              }
            }
          }

          merge(obj, validatePostalCode)
        }

        merge(rules, obj)
      }
    })
    return { form: rules }
  },
  data () {
    return {
      Countries: CountryNames,
      form: {
        building: '',
        floor: '',
        streetType: '',
        street: '',
        houseNumber: null,
        annex: '',
        postalCode: '',
        city: '',
        region: '',
        country: this.countryCode
      },
      invalidForm: false,
      FormFields
    }
  },
  computed: {
    formElements () {
      const form = []

      const formFields = this.FormFields[this.countryCode] ? this.FormFields[this.countryCode] : this.FormFields.nl

      formFields.fields.forEach((element) => {
        if (element.show) {
          form.push(element)
        }
      })

      return form
    },
    countryList () {
      return Object.keys(CountryNames).map(country => ({
        svg: country,
        label: this.$t('Countries.' + country),
        value: country
      }))
    },
    loqateLabelText () {
      if (this.countryCode === CountryCodes.ireland) {
        return 'SignUp.StepFour.FindYourAddressByIercode'
      }

      return 'SignUp.StepFour.FindYourAddress'
    },
    selectedCountry () {
      return this.countryList.find(obj => obj.value === this.countryCode)
    },
    isAddressFormShown () {
      return this.showAddressForm || this.alwaysShowAddressForm
    }
  },
  watch: {
    address: {
      handler (newAddress) {
        this.setAddress(newAddress)
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    onChange ($event, id = null) {
      this.$v.$touch()

      if (this.$v.$invalid && !this.invalidForm) {
        this.invalidForm = true
      } else if (this.invalidForm) {
        this.invalidForm = false
      }

      const field = !id ? $event.target.id : id

      const address = {
        ...this.form,
        [field]: $event.target.value
      }

      this.$emit('change', { field, address })
    },
    onCancel () {
      if (typeof this.cancelLink === 'string') {
        this.$router.push(this.cancelLink)
        return
      }

      this.$emit('cancel')
    },
    getErrorMessage (item) {
      if (!this.$v.form[item.model].$invalid || !this.invalidForm) {
        return ''
      }

      let errorMessage = this.$t('Error.invalid', { label: this.$t(`Form.Address.${item.model}`) })

      const validations = {
        required: this.$t('Form.Error.Required'),
        numeric: this.$t('Form.Error.Numeric'),
        maxLength: this.$t('Form.Error.MaxLength', { value: item.validations.maxLength })
      }

      Object.keys(validations).forEach((rule) => {
        if (typeof this.$v.form[item.model][rule] !== 'undefined' && !this.$v.form[item.model][rule]) {
          errorMessage = validations[rule]
        }
      })

      return errorMessage
    },
    setAddress (newAddress) {
      const address = newAddress ?? this.address

      if (address) {
        const { streetType, building, floor, street, houseNumber, annex, postalCode, city, region } = address

        this.form.streetType = streetType
        this.form.building = building
        this.form.floor = floor
        this.form.street = street
        this.form.houseNumber = houseNumber
        this.form.annex = annex
        this.form.postalCode = postalCode
        this.form.city = city
        this.form.region = region
        this.form.country = this.countryCode
      } else {
        this.form = {
          streetType: '',
          building: '',
          floor: '',
          street: '',
          houseNumber: null,
          annex: '',
          postalCode: '',
          city: '',
          region: '',
          country: this.countryCode
        }
      }
    },
    setGridArea (field) {
      return {
        gridArea: field
      }
    },
    setInputLabel (field) {
      const tField = (field === 'annex' && this.countryCode === CountryCodes.belgium) ? `${field}Be` : field

      return this.$t(`Form.Address.${tField}`)
    },
    getAddress () {
      const address = { ...this.form }
      return { address, country: this.countryCode }
    },
    onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.invalidForm = true

        let msg = this.$t('Error.AddressGeneral')

        // story: OTA-2055
        if (this.$v.form.houseNumber.$anyError && this.disabled) {
          msg = this.$t('Form.Error.EnterManually')
        }

        this.$otoaster.danger(msg)
      } else {
        const address = { ...this.form }

        this.$emit('submit', { address, country: this.countryCode })
      }
    }
  }
}
</script>

<style lang="postcss" scoped>

.address-grid {
  @apply grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto;
  gap: 1rem 2rem;
  margin-bottom: 1rem;

  &-nl {
    grid-template-areas: "street street" "houseNumber annex" "postalCode postalCode" "city city";
  }

  &-be {
    grid-template-areas: "building floor" "street street" "houseNumber annex" "postalCode ." "city city";
  }

  &-de {
    grid-template-areas: "street street" "houseNumber annex" "postalCode ." "city city";
  }

  &-at {
    grid-template-areas: "street street" "houseNumber annex" "postalCode ." "city city";
  }

  &-gb {
    grid-template-areas: "houseNumber building" "street street" "annex annex" "city city" "region region" "postalCode postalCode";
  }

  &-fr {
    grid-template-areas: "houseNumber ." "street street" "annex annex" "postalCode ." "city city";
  }

  &-se {
    grid-template-areas: "street street" "houseNumber ." "postalCode postalCode" "city city";
  }

  &-dk {
    grid-template-areas: "street street" "houseNumber annex" "postalCode postalCode" "city city";
  }

  &-es {
    grid-template-areas: "streetType streetType" "street street" "building floor" "houseNumber ."  "postalCode postalCode" "city city" "region region";
  }

  &-it {
    grid-template-areas: "street street" "houseNumber annex" "postalCode postalCode" "city city" "region region";
  }

  &-pl {
    grid-template-areas: "street street" "houseNumber annex" "postalCode postalCode" "city city" "region region";
  }

  &-ch {
    grid-template-areas: "street street" "houseNumber annex" "postalCode postalCode" "city city";
  }

  &-ie {
    grid-template-areas: "houseNumber building" "street street" "region region" "city city" "postalCode postalCode";
  }

  &-lu {
    grid-template-areas: "houseNumber houseNumber" "street street" "postalCode postalCode" "city city";
  }

  &-fi {
    grid-template-areas: "street street" "houseNumber annex" "postalCode postalCode" "city city";
  }

  &-pt {
    grid-template-areas: "street street" "houseNumber annex" "postalCode postalCode" "city city" "region region";
  }

  &-no {
    grid-template-areas: "street street" "houseNumber annex" "postalCode postalCode" "city city";
  }

  &-cz {
    grid-template-areas: "street street" "houseNumber annex" "postalCode postalCode" "city city";
  }
}

</style>
